import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  categoriesQuery,
  createCategory,
  createProduct,
  productsQuery,
  stockLocationsQuery,
  addHomeLocation,
  type NewProduct,
  type Product,
} from '../api/inventory.ts';
import { meQuery } from '../api/me.ts';
import { venueSettingsQuery, venuesQuery } from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { AddButton } from '../components/action-buttons.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { LocationsSection } from './locations-section.tsx';
import { dollarsToMinorUnits } from '../money.ts';

// CB-REQ INV-A01 and INV-A02 (v1.940): the catalogue. A product is what gets quoted and reserved;
// its serials live on the product's own page. Stock on hand is the database's count, not this
// screen's — a serial in repair is already out of it.
//
// INV-A14 at v1.982 (VENUE EQUIP): equipment a venue owns is recorded here too, tagged with its venue
// and left out of the company's own figures. The list opens on the company's own.

type Owner = 'ours' | 'venue' | 'all';
const OWNERS: ReadonlyArray<[Owner, string]> = [
  ['ours', 'Ours'],
  ['venue', 'Venue equipment'],
  ['all', 'All'],
];

export function InventoryScreen() {
  const products = useQuery(productsQuery);
  const me = useQuery(meQuery);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [owner, setOwner] = useState<Owner>('ours');
  const venues = useQuery(venuesQuery);
  const venueName = new Map((venues.data ?? []).map((v) => [v.id, v.name]));

  // The API refuses regardless (FND-01); hiding the control only spares an invitation to fail.
  const mayManage = me.data?.capabilities.includes('inventory.manage') ?? false;
  const seesCosts = me.data?.capabilities.includes('inventory.view-costs') ?? false;

  const everything = products.data ?? [];
  const anyVenueEquipment = everything.some((p) => p.ownerVenueId);
  const rows = everything.filter((p) =>
    owner === 'all' ? true : owner === 'venue' ? !!p.ownerVenueId : !p.ownerVenueId,
  );
  const categories = [
    ...new Set(rows.map((p) => p.category?.name).filter((c) => c !== undefined)),
  ].sort();
  const needle = search.trim().toLowerCase();
  const shown = rows.filter(
    (p) =>
      (category === null || p.category?.name === category) &&
      (needle === '' ||
        [p.name, p.manufacturer, p.model, p.category?.name]
          .filter(Boolean)
          .some((text) => text!.toLowerCase().includes(needle))),
  );
  const onHand = rows.reduce((sum, p) => sum + p.stockOnHand, 0);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Inventory"
        title="Catalogue"
        meta={
          products.data
            ? `${rows.length} ${rows.length === 1 ? 'product' : 'products'} · ${onHand} on hand`
            : undefined
        }
        actions={
          mayManage && !adding ? <AddButton noun="Product" onClick={() => setAdding(true)} /> : null
        }
      />

      {adding ? <NewProductForm seesCosts={seesCosts} onDone={() => setAdding(false)} /> : null}

      {products.isPending ? <p className="text-muted">Loading…</p> : null}
      {products.isError ? (
        <p role="alert" className="alert">
          {products.error.message}
        </p>
      ) : null}
      {products.data?.length === 0 ? (
        <p className="text-ink-soft">
          No products yet.{' '}
          {mayManage
            ? 'Add the first one above.'
            : 'Someone with permission can add the first one.'}
        </p>
      ) : null}

      {anyVenueEquipment ? (
        <div
          role="group"
          aria-label="Whose"
          className="flex w-fit flex-wrap overflow-hidden rounded-md border border-divider"
        >
          {OWNERS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={owner === value}
              onClick={() => setOwner(value)}
              className="cursor-pointer border-r border-divider px-3 py-1.5 text-sm last:border-r-0 hover:bg-ink/7 aria-pressed:bg-accent-600 aria-pressed:text-white"
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <>
          <dl className="stat-grid grid-cols-2 md:grid-cols-4">
            <Stat label="Products" value={rows.length} />
            <Stat label="On hand" value={onHand} note="serviceable units" />
            <Stat
              label="By serial"
              value={rows.filter((p) => p.tracking === 'serialised').length}
              note="products"
            />
            <Stat
              label="By quantity"
              value={rows.filter((p) => p.tracking === 'bulk').length}
              note="products"
            />
          </dl>

          <div className="flex flex-wrap items-center gap-3">
            <label className="min-w-64 flex-1">
              <span className="sr-only">Search the catalogue</span>
              <input
                type="search"
                className="input"
                placeholder="Search — name, manufacturer, model, category"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            </label>
            {categories.length > 0 ? (
              <div
                role="group"
                aria-label="Category"
                className="flex flex-wrap overflow-hidden rounded-md border border-divider"
              >
                {[null, ...categories].map((c) => (
                  <button
                    key={c ?? 'all'}
                    type="button"
                    aria-pressed={category === c}
                    onClick={() => setCategory(c)}
                    className="cursor-pointer border-r border-divider px-3 py-1.5 text-sm last:border-r-0 hover:bg-ink/7 aria-pressed:bg-accent-600 aria-pressed:text-white"
                  >
                    {c ?? 'All'}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {shown.length > 0 ? (
            <ProductTable rows={shown} venueName={venueName} />
          ) : (
            <p className="text-ink-soft">Nothing matches that search.</p>
          )}
        </>
      ) : null}

      <InventoryLocations mayManage={mayManage} />
    </div>
  );
}

/** INV-A09 at v1.986: the Locations section, fed from the API. */
function InventoryLocations({ mayManage }: { mayManage: boolean }) {
  const locations = useQuery(stockLocationsQuery);
  const queryClient = useQueryClient();
  const add = useMutation({
    mutationFn: ({ locationId, from }: { locationId: string; from: string }) =>
      addHomeLocation(locationId, from),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: stockLocationsQuery.queryKey }),
  });
  if (!locations.data) return null;
  return (
    <LocationsSection
      locations={locations.data}
      mayManage={mayManage}
      saving={add.isPending}
      error={add.error}
      onAdd={(locationId, from) => add.mutateAsync({ locationId, from })}
    />
  );
}

function Stat({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="stat">
      <dt className="kicker">{label}</dt>
      <dd>
        <span className="stat-value block">{value}</span>
        {note ? <span className="mt-1 block text-xs text-muted">{note}</span> : null}
      </dd>
    </div>
  );
}

function ProductTable({
  rows,
  venueName,
}: {
  rows: Product[];
  venueName: ReadonlyMap<string, string>;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <caption className="sr-only">
          Products, with how each is counted and how many are on hand
        </caption>
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col">Category</th>
            <th scope="col">Counted by</th>
            <th scope="col" className="text-right">
              On hand
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id}>
              <th scope="row" className="font-semibold">
                <Link to="/inventory/$productId" params={{ productId: p.id }} className="link">
                  {p.name}
                </Link>
                {p.ownerVenueId ? (
                  <span className="tag ml-2 align-middle">
                    VENUE EQUIP · {venueName.get(p.ownerVenueId) ?? 'a venue'}
                  </span>
                ) : null}
                {p.manufacturer || p.model ? (
                  <span className="block text-sm font-normal text-muted">
                    {[p.manufacturer, p.model].filter(Boolean).join(' ')}
                  </span>
                ) : null}
              </th>
              <td>
                {p.category ? (
                  <span className="tag">{p.category.name}</span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="text-ink-soft">
                {p.tracking === 'serialised'
                  ? 'Serial or tag'
                  : p.tracking === 'bulk'
                    ? 'Quantity'
                    : 'Service — no stock'}
              </td>
              <td className="text-right text-lg font-extrabold tabular-nums">{p.stockOnHand}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewProductForm({ seesCosts, onDone }: { seesCosts: boolean; onDone: () => void }) {
  const queryClient = useQueryClient();
  const categories = useQuery(categoriesQuery);
  const [name, setName] = useState('');
  const [tracking, setTracking] = useState<'serialised' | 'bulk' | 'service'>('serialised');
  const [categoryId, setCategoryId] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [openingQuantity, setOpeningQuantity] = useState('');
  const [replacementCost, setReplacementCost] = useState('');
  const [ownerVenueId, setOwnerVenueId] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  // INV-A14 at v1.982: a venue's own equipment, offered only while the company records it.
  const settings = useQuery(venueSettingsQuery);
  const venues = useQuery(venuesQuery);
  const offersVenue = settings.data?.venueEquipment === true && (venues.data?.length ?? 0) > 0;
  const forVenue = offersVenue && ownerVenueId !== '';

  const save = useMutation({
    mutationFn: async (input: NewProduct & { newCategory?: string }) => {
      const { newCategory: categoryName, ...product } = input;
      // A category typed here is created first, so the product can name it. Not one transaction:
      // if the product then fails, the category stays and is simply there to pick next time.
      if (categoryName) product.categoryId = (await createCategory({ name: categoryName })).id;
      return createProduct(product);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: productsQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: categoriesQuery.queryKey });
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);
    const cost = replacementCost.trim() ? dollarsToMinorUnits(replacementCost) : undefined;
    if (cost === null) {
      setLocalError('Replacement cost should be an amount in dollars, like 1299.50.');
      return;
    }
    if (forVenue && tracking === 'service') {
      setLocalError('A venue’s equipment is counted by serial or by quantity, not a service.');
      return;
    }
    save.mutate({
      name: name.trim(),
      tracking,
      ...(forVenue ? { ownerVenueId } : {}),
      ...(categoryId && categoryId !== 'new' ? { categoryId } : {}),
      ...(categoryId === 'new' && newCategory.trim() ? { newCategory: newCategory.trim() } : {}),
      ...(manufacturer.trim() ? { manufacturer: manufacturer.trim() } : {}),
      ...(model.trim() ? { model: model.trim() } : {}),
      ...(tracking === 'bulk' && openingQuantity
        ? { openingQuantity: Number(openingQuantity) }
        : {}),
      ...(cost && !forVenue
        ? { replacementCost: { amountMinor: cost, currency: 'AUD' as const } }
        : {}),
    });
  }

  const error = localError ?? (save.isError ? save.error.message : null);

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4">
      <h2 className="section-title">Add a product</h2>
      {error ? (
        <p role="alert" className="alert">
          {error}
        </p>
      ) : null}

      <Field label="Name" required value={name} onChange={(e) => setName(e.currentTarget.value)} />

      {offersVenue ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="owner" className="text-sm font-medium text-ink">
            Who owns it
          </label>
          <select
            id="owner"
            value={ownerVenueId}
            onChange={(e) => {
              setOwnerVenueId(e.currentTarget.value);
              if (e.currentTarget.value && tracking === 'service') setTracking('serialised');
            }}
            className="input"
          >
            <option value="">Us</option>
            {(venues.data ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
          {forVenue ? (
            <p className="text-sm text-muted">
              Kept at the venue, quoted only there and at no charge, and left out of your stock
              value.
            </p>
          ) : null}
        </div>
      ) : null}

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-ink">How is it counted?</legend>
        <p className="text-sm text-muted">This cannot be changed once the product is saved.</p>
        {(
          [
            ['serialised', 'By serial number — each unit tracked on its own'],
            ['bulk', 'By quantity — cable, stands, anything without a serial'],
            // INV-A02 at v1.943: sold rather than hired, so it holds no stock at all.
            ['service', 'It is a service — delivery, labour, travel — with no stock'],
          ] as const
        )
          .filter(([value]) => !(forVenue && value === 'service'))
          .map(([value, label]) => (
            <label key={value} className="flex items-center gap-2 text-ink">
              <input
                type="radio"
                name="tracking"
                value={value}
                checked={tracking === value}
                onChange={() => setTracking(value)}
              />
              {label}
            </label>
          ))}
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="category" className="text-sm font-medium text-ink">
          Category (optional)
        </label>
        <select
          id="category"
          value={categoryId}
          onChange={(e) => setCategoryId(e.currentTarget.value)}
          className="input"
        >
          <option value="">None</option>
          {(categories.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
          <option value="new">+ New category</option>
        </select>
      </div>
      {categoryId === 'new' ? (
        <Field
          label="New category name"
          required
          value={newCategory}
          onChange={(e) => setNewCategory(e.currentTarget.value)}
        />
      ) : null}

      <Field
        label="Manufacturer (optional)"
        value={manufacturer}
        onChange={(e) => setManufacturer(e.currentTarget.value)}
      />
      <Field
        label="Model (optional)"
        value={model}
        onChange={(e) => setModel(e.currentTarget.value)}
      />

      {tracking === 'bulk' ? (
        <Field
          label="How many are on hand now?"
          hint="Recorded as the opening balance. Serialised products are counted by their serials instead."
          type="number"
          min={0}
          value={openingQuantity}
          onChange={(e) => setOpeningQuantity(e.currentTarget.value)}
        />
      ) : null}

      {/* INV-A01: above the price line. Not offered to anyone the API would refuse it from. */}
      {seesCosts && !forVenue ? (
        <Field
          label="Replacement cost, AUD (optional)"
          inputMode="decimal"
          value={replacementCost}
          onChange={(e) => setReplacementCost(e.currentTarget.value)}
        />
      ) : null}

      <div className="flex gap-3">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
